import { joiResolver } from '@hookform/resolvers/joi';
import { useForm, UseFormReturn } from 'react-hook-form';
import dayjs from 'dayjs';
import { stringToPayloadFormat } from 'web-library';
import { EditOpenConfigDate } from '../../../models';
import openPeriodSchema from '../schema/openPeriodSchema';
import { candidateConfigQuery } from '../../../api/candidate/config';

export interface EditDeleteReturn {
  /**
   * Perform submit action, either to call block call or unblock call, depends on the account status
   */
  submitUpdate: (submitData: any) => Promise<void>;
  /**
   * Form to send edit & delete
   */
  form: UseFormReturn<Record<string, any>>;

  /**
   * React query mutation object
   */

  updateOpenPeriodMutation: any;
}

/**
 * A hook to handle edit functionality
 * @param configurationType Configurator to be updated
 * @param examLevelCode Exam level to be updated
 * @param lastUpdatedBy Date time ops admin updated the config
 * @param lastUpdatedAt User that updated the configuration
 * @returns
 */
export const useFeatureConfiguration = (
  configurationType: string,
  examLevelCode: string,
  lastUpdatedBy: string,
  lastUpdatedAt: string
): EditDeleteReturn => {
  const form = useForm({
    mode: 'onSubmit',
    resolver: joiResolver(openPeriodSchema),
  });

  const dateTimeFormat = 'YYYY-MM-DDhh:mm';

  const updateOpenPeriodMutation = candidateConfigQuery.useUpdateOpenPeriodConfig(
    () => {
      // do nothing
    },
    () => {
      // do nothing
    }
  );

  // Validate Time
  const validateTime = (startDate: any, endDate: any, startTime: any, endTime: any) => {
    const start = stringToPayloadFormat(startDate);
    const startDateTime = start.concat(startTime);
    const end = stringToPayloadFormat(endDate);
    const endDateTime = end.concat(endTime);

    const startHour = dayjs(startDateTime, dateTimeFormat);
    const endHour = dayjs(endDateTime, dateTimeFormat);

    if(start !== end && endHour.isBefore(startHour)){
      form.setError('endDate', {
        type: 'manual',
        message: 'End Date should be greater than Start Date',
      });
      return false;
    }

    if (start === end && endHour.isBefore(startHour)) {
      form.setError('endTime', {
        type: 'manual',
        message: 'End Time should be greater than Start Time',
      });
      return false;
    }
    return true;
  };

  const submitUpdate = async (submitData: EditOpenConfigDate): Promise<void> => {
    const isValid = validateTime(
      submitData.startDate,
      submitData.endDate,
      submitData.startTime,
      submitData.endTime
    );
    if (isValid) {
      const startDateTime = submitData.startDate.concat(submitData.startTime);
      const endDateTime = submitData.endDate.concat(submitData.endTime);
      updateOpenPeriodMutation.mutateAsync({
        configurationType,
        examLevelCode,
        lastUpdatedBy,
        lastUpdatedAt,
        startDateTime: dayjs(startDateTime, dateTimeFormat).format('YYYY-MM-DDTHH:mm:ss'),
        endDateTime: dayjs(endDateTime, dateTimeFormat).format('YYYY-MM-DDTHH:mm:ss'),
      });
    }
  };

  return {
    form,
    submitUpdate,
    updateOpenPeriodMutation,
  };
};
